/** Minimal i18n for the PoC.
 *
 * Locale is taken from the browser / POS WebView (`navigator.language`), falling back to English.
 * Only the app's OWN static strings live here — merchant content (reward names, card labels, …)
 * arrives already localized from the backend, so it is never translated client-side.
 *
 * Supported: English (fallback) + French. Add a locale by extending `STRINGS` and `getLocale`.
 */
export type Locale = 'en' | 'fr'

const STRINGS = {
  en: {
    rewardGiven: 'Reward Given',
    pointsGiven: 'Points Sent',
    tapToContinue: 'Tap anywhere to continue',
    points: 'Points',
    availableRewards: 'Available rewards',
    otherActions: 'Other actions',
    givePoint: 'Give a point',
    statistics: 'Statistics',
    visits: 'Visits',
    validUntil: 'Valid until',
    done: 'Done',
    close: 'Close',
    redeem: 'Redeem',
    redeemed: 'Redeemed',
    applied: 'Applied',
    saving: 'Saving…',
    recording: 'Recording…',
    scanInstruction: "Point the camera at the customer's Badger QR code.",
    // v3 redesign (Figma section 2653:9217).
    v3ScanPrompt: 'Press to scan QR code',
    v3Customers: 'Customers',
    v3TopCustomers: 'Top Customers',
    v3Recent: 'Recent',
    v3SearchCustomer: 'Search Customer',
    v3LastSeen: 'Last Seen',
    v3Tab_profile: 'Profile',
    v3Tab_history: 'History',
    v3Tab_tools: 'Tools',
    v3UnlinkCustomer: 'Unlink Customer',
    v3LinkedCustomer: 'Linked customer · points awarded at checkout',
    v3Username: 'Username',
    v3PointsAvailable: 'Points Available',
    v3AfterPayment: 'After Payment',
    v3Total: 'Total',
    v3CustomerInfo: 'Customer Info',
    v3ClientSince: 'Client since',
    v3RewardsClaimed: 'Rewards Claimed',
    v3NeedsSignUp: 'Needs to sign-up',
    v3Use: 'Use',
    v3Unavailable: 'Unavailable',
    v3BirthdayReward: 'Birthday Reward !',
    v3ActivateReward: 'Activate Reward',
    v3WhichItem: 'Which Item ?',
    v3ApplyingTo: 'Applying to',
    v3SelectOther: 'Select Other',
    v3Cancel: 'Cancel',
    v3Confirm: 'Confirm',
    v3BillingBlockedTitle: 'Contact your manager.',
    v3BillingBlockedBody: 'You’ll need to update your billing information to continue using Badger.',
    v3CustomerSupport: 'Customer Support',
    v3CustomersUseProgram: 'Customers use your loyalty program',
    v3UpdateBillingToContinue: 'Update your billing to continue',
    v3BillingLateWarning: 'Payment overdue — ask your manager to update billing',
    v3Search: 'Search',
    v3SearchPlaceholder: 'Phone, Name, E-mail',
    v3BackToScan: 'Back to scan',
    v3Email: 'E-mail',
    v3Phone: 'Phone',
    v3Amount: 'Amount',
    v3CurrentBalance: 'Current Balance',
    v3SendPoints: 'Send Points',
    v3Backspace: 'Backspace',
    v3Transactions: 'Transactions',
    v3Earned: 'Earned',
    v3Spent: 'Spent',
    v3NoTransactions: 'No activity yet',
    v3TransactionHistoryFor: 'Transaction history for',
    v3Today: 'Today',
    v3Yesterday: 'Yesterday',
    v3PtsShort: 'pts',
    v3Ok: 'OK',
    v3GiveByHand: 'Give it by hand',
    v3InsertPassword: 'Insert Password',
    v3SendExtraPoints: 'Send extra points',
    v3ErrDiscountAppliedTitle: 'Discount already applied',
    v3ErrDiscountAppliedBody: 'Only one reward can be used per sale. Remove the current one first if you want to change it.',
    v3ErrLowBalanceTitle: 'Not enough points',
    v3ErrLowBalanceBody: 'This customer does not have enough points for that reward yet.',
    v3ErrPriceChangedTitle: 'The price changed',
    v3ErrPriceChangedBody: 'This reward now costs a different number of points. Check the new cost with the customer before confirming.',
    v3ErrRewardGoneTitle: 'Reward unavailable',
    v3ErrRewardGoneBody: 'This reward is no longer offered here.',
    v3ErrClaimExpiredTitle: 'Reward expired',
    v3ErrClaimExpiredBody: 'The reward was held too long and the points went back to the customer. Start again.',
    v3ErrAlreadySettledTitle: 'Already done',
    v3ErrAlreadySettledBody: 'This reward was already applied to the sale. Nothing more to do.',
    v3ErrManualTitle: 'Apply the reward yourself',
    v3ErrManualBody: 'The discount could not be applied automatically. Hand the reward over and record it here, and the points will settle when the sale is paid.',
    v3ErrOutageTitle: 'Badger is unreachable',
    v3ErrOutageBody: 'Points cannot be used right now. Purchases will still be credited once the connection returns.',
    v3ErrNotReadyTitle: 'Not set up yet',
    v3ErrNotReadyBody: 'This location is not ready for loyalty. Ask your manager to finish the setup in the dashboard.',
    v3ErrLateTitle: 'Payment overdue',
    v3ErrStaleTitle: 'Something changed',
    v3ErrStaleBody: 'The customer or the programme changed while you were on this screen. Try again.',
    v3ErrQrTitle: 'Wrong code',
    v3ErrQrBody: 'That is not a Badger customer code.',
    v3ErrUpgradeTitle: 'Update needed',
    v3ErrUpgradeBody: 'This till is running an old version of Badger. Ask your manager to update it.',
    v3ErrUnknownTitle: 'Something went wrong',
    v3ErrUnknownBody: 'The action could not be completed. Try again, and tell support the code below if it keeps happening.',
    // The same failure with nothing to report. Pointing at a code that is not on screen
    // sends someone to a support call missing the one detail the app asked them for.
    v3ErrUnknownBodyNoCode: 'The action could not be completed. Try again, and contact support if it keeps happening.',
    v3ErrTillAuthTitle: 'This till is not linked',
    v3ErrTillAuthBody: 'Badger did not recognise this till. Ask your manager to re-link it from the dashboard. Serve the customer as usual — the purchase will be credited once the till is linked again.',
    loading: 'Loading…',
    startingCamera: 'Starting camera…',
    changeCamera: 'Change camera',
    cancel: 'Cancel',
    applyReward: 'Apply reward',
    applyDiscountWhere: 'Where would you like to apply the « {reward} » discount?',
    rewardWhichItem: 'Which item would you like to reward?',
    saleEmpty: 'Open sale is empty.',
    applying: 'Applying…',
    wholeAccount: 'On Everything',
    item: 'Item',
    loadingCustomer: 'Loading customer…',
    somethingWrong: 'Something went wrong',
    tryAgain: 'Try again',
    scanCardTitle: 'Scan customer card',
    scanCardHint: 'Scan a card, or enter the code manually.',
    scanPlaceholder: 'Scan or enter code…',
    lookUpCustomer: 'Look up customer',
    scanWithCamera: 'Scan with camera',
    cardCodeLabel: 'Customer card code',
    camPermissionDenied: 'Camera permission denied.',
    camNotFound: 'No camera found on this device.',
    camInUse: 'Camera is already in use by another app.',
    camGeneric: 'Could not start the camera.',
    camInsecure: 'Camera needs a secure (HTTPS) context.',
    camUnavailable: 'Camera not available in this web view.',
    camCanvas: 'Canvas not supported in this web view.',
    camError: 'Camera error',
    updateTitle: 'Update this app?',
    updateBody: 'Reload and empty the cache to fetch the latest version.',
    updateCurrent: 'current',
    update: 'Update',
    customer: 'Customer',
    pts: 'pts',
    linkedPointsOnPayment: 'Customer linked · points credited on payment',
    marketplaceClaimableNow: 'MARKETPLACE — CLAIMABLE NOW',
    insufficientPoints: 'Not enough points',
    rewardLocked: 'Price changed — locked',
    claimed: 'Claimed',
    addPoints: 'Add points',
    useRewardQuestion: 'Use this reward?',
    balanceAfter: 'Balance after',
    offerProductInOrder: 'Give the product in the order',
    addProductToOrder: 'Add the product to the order',
    discountDidNotApply:
      'The discount did not apply. Check that the BADGER_FREE discount exists in your Lightspeed back office.',
    itemNotAdded:
      'The product could not be added to the order. Add it on the till, then give it from here.',
    itemMissingPosId:
      'This reward has no Lightspeed item id yet, so it cannot be rung up. Add the product on the till, then give it from here.',
    voidAddedLine:
      'You must remove «{item}» from the basket so the customer is not charged for it.',
    actionOnTill: 'Remove the product from the basket',
    discountStillApplied: 'The discount is still on the sale. Remove it on the till before continuing.',
    rewardToLink: 'Reward to link',
    rewardNotLinkedYet: '«{reward}» is not linked to a Lightspeed discount yet.',
    rewardLinkThenUse: 'Link it — the reward will be used right after.',
    stepChooseItem: '1 · CHOOSE THE ITEM',
    stepChooseDiscount: '2 · CHOOSE THE LIGHTSPEED DISCOUNT',
    searchItemPlaceholder: 'Search an item…',
    noItemsFound: 'No matching item.',
    discountsFromBackOffice: 'List read from your Lightspeed back office',
    rewardAppliedToThisItem: 'the reward will be applied to this item',
    pickItemAndDiscount: 'Pick an item and a discount to link them.',
    cannotFindDiscount: "I can't find the right discount",
    linkAndUseReward: 'Link and use the reward',
    manualAttribution: 'Manual attribution',
    linked: 'linked',
    amountSpent: 'AMOUNT SPENT',
    pointsToGive: 'POINTS TO GIVE',
    entryMode: 'Entry mode',
    fixedRule: 'Fixed rule: 1 CHF = 10 pts · taken to the 10-cent step',
    attributePoints: 'Give the points',
    backspace: 'Delete',
    claimedTapToUndo: 'Tap to undo',
    undoRewardQuestion: 'Undo this reward?',
    undoReward: 'Give the points back',
    back: 'Back',
    menuUnavailable: 'Menu not synced yet — no item to link to.',
    whichLineToOffer: 'Which item to give?',
    whichLineToOfferHint:
      'The order has several of this product. Pick the one «{reward}» applies to.',
    orderLineNumber: 'Line {n}',
    tapToStartScan: 'Tap to start the scan',
    scanCustomerQr: "Scan the customer's Badger QR",
    scanCameraHint: 'Rear camera · you can link at any time',
    searchCustomer: 'Look up a customer',
    searchPhonePlaceholder: 'Phone number',
    searchNamePlaceholder: 'Name',
    searchEmailPlaceholder: 'E-mail',
    recents: 'Recent',
    results: 'Results',
    noCustomerFound: 'No matching customer.',
    noDiscountsConfigured: 'No discount configured in your Lightspeed back office.',
  },
  fr: {
    rewardGiven: 'Récompense offerte',
    pointsGiven: 'Points Envoyés',
    tapToContinue: 'Touchez pour continuer',
    points: 'Points',
    availableRewards: 'Récompenses Disponibles',
    otherActions: 'Autres actions',
    givePoint: 'Donner un Point',
    statistics: 'Statistiques',
    visits: 'Visites',
    validUntil: 'Valable jusqu’au',
    done: 'Terminé',
    close: 'Fermer',
    redeem: 'Utiliser',
    redeemed: 'Utilisée',
    applied: 'Appliqué',
    saving: 'Enregistrement…',
    recording: 'Enregistrement…',
    scanInstruction: 'Pointez la caméra vers le QR Badger du client.',
    // v3 redesign (Figma section 2653:9217). The mockups mix English and French labels;
    // both locales are filled in so neither renders a key or an English word in a
    // French till.
    v3ScanPrompt: 'Appuyez pour scanner le QR',
    v3Customers: 'Clients',
    v3TopCustomers: 'Meilleurs clients',
    v3Recent: 'Récents',
    v3SearchCustomer: 'Rechercher un client',
    v3LastSeen: 'Vu le',
    v3Tab_profile: 'Profil',
    v3Tab_history: 'Historique',
    v3Tab_tools: 'Outils',
    v3UnlinkCustomer: 'Désassocier client',
    v3LinkedCustomer: 'Client associé · points crédités au paiement',
    v3Username: "Nom d'utilisateur",
    v3PointsAvailable: 'Points disponibles',
    v3AfterPayment: 'Après paiement',
    v3Total: 'Total',
    v3CustomerInfo: 'Infos client',
    v3ClientSince: 'Client depuis',
    v3RewardsClaimed: 'Récompenses utilisées',
    v3NeedsSignUp: "Doit finaliser son inscription",
    v3Use: 'Utiliser',
    v3Unavailable: 'Indisponible',
    v3BirthdayReward: 'Récompense anniversaire !',
    v3ActivateReward: 'Activer la récompense',
    v3WhichItem: 'Quel article ?',
    v3ApplyingTo: 'Appliquer à',
    v3SelectOther: 'Choisir un autre',
    v3Cancel: 'Annuler',
    v3Confirm: 'Confirmer',
    v3BillingBlockedTitle: 'Contactez votre responsable.',
    v3BillingBlockedBody: 'Vous devez mettre à jour vos informations de facturation pour continuer à utiliser Badger.',
    v3CustomerSupport: 'Service client',
    v3CustomersUseProgram: 'clients utilisent votre programme de fidélité',
    v3UpdateBillingToContinue: 'Mettez à jour votre facturation pour continuer',
    v3BillingLateWarning: 'Paiement en retard — demandez à votre responsable de régulariser',
    v3Search: 'Rechercher',
    v3SearchPlaceholder: 'Téléphone, nom, e-mail',
    v3BackToScan: 'Retour au scan',
    v3Email: 'E-mail',
    v3Phone: 'Téléphone',
    v3Amount: 'Montant',
    v3CurrentBalance: 'Solde actuel',
    v3SendPoints: 'Envoyer les points',
    v3Backspace: 'Effacer',
    v3Transactions: 'Transactions',
    v3Earned: 'Gagnés',
    v3Spent: 'Utilisés',
    v3NoTransactions: 'Aucune activité',
    v3TransactionHistoryFor: 'Historique des transactions de',
    v3Today: "Aujourd'hui",
    v3Yesterday: 'Hier',
    v3PtsShort: 'pts',
    v3Ok: 'OK',
    v3GiveByHand: 'Donner à la main',
    v3InsertPassword: 'Saisir le code',
    v3SendExtraPoints: 'Envoyer des points',
    v3ErrDiscountAppliedTitle: 'Remise déjà appliquée',
    v3ErrDiscountAppliedBody: "Une seule récompense par vente. Retirez d'abord l'actuelle pour la changer.",
    v3ErrLowBalanceTitle: 'Points insuffisants',
    v3ErrLowBalanceBody: "Ce client n'a pas encore assez de points pour cette récompense.",
    v3ErrPriceChangedTitle: 'Le prix a changé',
    v3ErrPriceChangedBody: 'Cette récompense coûte désormais un autre nombre de points. Vérifiez le nouveau coût avec le client avant de confirmer.',
    v3ErrRewardGoneTitle: 'Récompense indisponible',
    v3ErrRewardGoneBody: "Cette récompense n'est plus proposée ici.",
    v3ErrClaimExpiredTitle: 'Récompense expirée',
    v3ErrClaimExpiredBody: 'La récompense a été réservée trop longtemps et les points sont revenus au client. Recommencez.',
    v3ErrAlreadySettledTitle: 'Déjà fait',
    v3ErrAlreadySettledBody: 'Cette récompense a déjà été appliquée à la vente. Rien à faire de plus.',
    v3ErrManualTitle: 'Appliquez la récompense vous-même',
    v3ErrManualBody: "La remise n'a pas pu être appliquée automatiquement. Remettez la récompense et enregistrez-le ici ; les points seront décomptés au paiement.",
    v3ErrOutageTitle: 'Badger est injoignable',
    v3ErrOutageBody: 'Les points ne peuvent pas être utilisés pour le moment. Les achats seront crédités dès le retour de la connexion.',
    v3ErrNotReadyTitle: 'Pas encore configuré',
    v3ErrNotReadyBody: "Cet établissement n'est pas prêt pour la fidélité. Demandez à votre responsable de terminer la configuration.",
    v3ErrLateTitle: 'Paiement en retard',
    v3ErrStaleTitle: 'Quelque chose a changé',
    v3ErrStaleBody: 'Le client ou le programme a changé pendant que vous étiez sur cet écran. Réessayez.',
    v3ErrQrTitle: 'Mauvais code',
    v3ErrQrBody: "Ce n'est pas un code client Badger.",
    v3ErrUpgradeTitle: 'Mise à jour nécessaire',
    v3ErrUpgradeBody: 'Cette caisse utilise une ancienne version de Badger. Demandez à votre responsable de la mettre à jour.',
    v3ErrUnknownTitle: "L'action a échoué",
    v3ErrUnknownBody: 'Réessayez, et communiquez le code ci-dessous au support si cela persiste.',
    v3ErrUnknownBodyNoCode: 'Réessayez, et contactez le support si cela persiste.',
    v3ErrTillAuthTitle: "Cette caisse n'est pas liée",
    v3ErrTillAuthBody:
      "Badger n'a pas reconnu cette caisse. Demandez à votre responsable de la relier depuis le back-office. Servez le client normalement — l'achat sera crédité une fois la caisse reliée.",
    loading: 'Chargement…',
    startingCamera: 'Démarrage de la caméra…',
    changeCamera: 'Changer caméra',
    cancel: 'Annuler',
    applyReward: 'Utiliser la récompense',
    applyDiscountWhere: 'Où appliquer la réduction « {reward} » ?',
    rewardWhichItem: 'Quel article souhaitez-vous offrir ?',
    saleEmpty: 'La vente est vide.',
    applying: 'Application…',
    wholeAccount: 'Sur tout',
    item: 'Article',
    loadingCustomer: 'Chargement du client…',
    somethingWrong: 'Une erreur est survenue',
    tryAgain: 'Réessayer',
    scanCardTitle: 'Scannez la carte client',
    scanCardHint: 'Scannez une carte ou saisissez le code.',
    scanPlaceholder: 'Scannez ou saisissez le code…',
    lookUpCustomer: 'Rechercher le client',
    scanWithCamera: 'Scanner avec la caméra',
    cardCodeLabel: 'Code de la carte client',
    camPermissionDenied: 'Accès à la caméra refusé.',
    camNotFound: 'Aucune caméra détectée sur cet appareil.',
    camInUse: 'La caméra est déjà utilisée par une autre application.',
    camGeneric: 'Impossible de démarrer la caméra.',
    camInsecure: 'La caméra nécessite un contexte sécurisé (HTTPS).',
    camUnavailable: 'Caméra indisponible dans cette vue.',
    camCanvas: 'Canvas non supporté dans cette vue.',
    camError: 'Erreur de caméra',
    updateTitle: 'Mettre à jour l’application ?',
    updateBody: 'Recharger et vider le cache pour récupérer la dernière version.',
    updateCurrent: 'actuelle',
    update: 'Mettre à jour',
    customer: 'Client',
    pts: 'pts',
    linkedPointsOnPayment: 'Client lié · points attribués au paiement',
    marketplaceClaimableNow: 'MARKETPLACE — RÉCLAMABLE MAINTENANT',
    insufficientPoints: 'Points insuffisants',
    rewardLocked: 'Prix modifié — bloqué',
    claimed: 'Réclamé',
    addPoints: 'Ajouter des points',
    useRewardQuestion: 'Utiliser la récompense ?',
    balanceAfter: 'Solde après',
    offerProductInOrder: 'Offrir le produit dans la commande',
    addProductToOrder: 'Ajouter le produit dans la commande',
    discountDidNotApply:
      'Le rabais ne s’est pas appliqué. Vérifiez que le rabais BADGER_FREE existe dans votre back office Lightspeed.',
    itemNotAdded:
      'Le produit n’a pas pu être ajouté à la commande. Ajoutez-le sur la caisse, puis offrez-le depuis ici.',
    itemMissingPosId:
      'Cette récompense n’a pas encore d’identifiant d’article Lightspeed, elle ne peut pas être encaissée. Ajoutez le produit sur la caisse, puis offrez-le depuis ici.',
    voidAddedLine:
      'Vous devez retirer «{item}» du panier pour ne pas le facturer au client.',
    actionOnTill: 'Retirez le produit du panier',
    discountStillApplied: 'Le rabais est toujours sur la vente. Retirez-le sur la caisse avant de continuer.',
    rewardToLink: 'Récompense à relier',
    rewardNotLinkedYet: '«{reward}» n’est pas encore relié à un rabais Lightspeed.',
    rewardLinkThenUse: 'Reliez-le — la récompense sera utilisée juste après.',
    stepChooseItem: '1 · CHOISISSEZ L’ARTICLE',
    stepChooseDiscount: '2 · CHOISISSEZ LE RABAIS LIGHTSPEED',
    searchItemPlaceholder: 'Rechercher un article…',
    noItemsFound: 'Aucun article correspondant.',
    discountsFromBackOffice: 'Liste lue depuis votre back-office Lightspeed',
    rewardAppliedToThisItem: 'la récompense sera appliquée sur cet article',
    pickItemAndDiscount: 'Choisissez un article et un rabais pour les relier.',
    cannotFindDiscount: 'Je ne trouve pas le bon rabais',
    linkAndUseReward: 'Relier et utiliser la récompense',
    manualAttribution: 'Attribution manuelle',
    linked: 'lié',
    amountSpent: 'MONTANT DÉPENSÉ',
    pointsToGive: 'POINTS À ATTRIBUER',
    entryMode: 'Mode de saisie',
    fixedRule: 'Règle fixe : 1 CHF = 10 pts · arrondi à la dizaine',
    attributePoints: 'Attribuer les points',
    backspace: 'Effacer',
    claimedTapToUndo: 'Toucher pour annuler',
    undoRewardQuestion: 'Annuler la récompense ?',
    undoReward: 'Rendre les points',
    back: 'Retour',
    menuUnavailable: 'Menu pas encore synchronisé — aucun article à relier.',
    whichLineToOffer: 'Quel article offrir ?',
    whichLineToOfferHint:
      'La commande contient plusieurs fois ce produit. Choisissez celui auquel «{reward}» s’applique.',
    orderLineNumber: 'Ligne {n}',
    tapToStartScan: 'Touchez pour démarrer le scan',
    scanCustomerQr: 'Scannez le QR Badger du client',
    scanCameraHint: 'Caméra arrière · le lien peut se faire à tout moment',
    searchCustomer: 'Rechercher un client',
    searchPhonePlaceholder: 'Numéro de téléphone',
    searchNamePlaceholder: 'Nom',
    searchEmailPlaceholder: 'E-mail',
    recents: 'Récents',
    results: 'Résultats',
    noCustomerFound: 'Aucun client correspondant.',
    noDiscountsConfigured: 'Aucun rabais configuré dans votre back-office Lightspeed.',
  },
} satisfies Record<Locale, Record<string, string>>

export type StringKey = keyof (typeof STRINGS)['en']

/** Resolve the active locale from the WebView language (e.g. "fr-CH" → "fr"). */
/** The active locale. Exported because dates, times and month names are formatted by
 * `Intl` rather than by a table here — a table would need translating four times and
 * keeping in step with the platform's own month names. See DEV-418 for the per-location
 * override that will eventually feed this. */
export function getLocale(): Locale {
  const lang = (typeof navigator !== 'undefined' ? navigator.language : 'en').toLowerCase()
  return lang.startsWith('fr') ? 'fr' : 'en'
}

/** Translate a static string key for the given (or detected) locale, falling back to English. */
export function t(key: StringKey, locale: Locale = getLocale()): string {
  return STRINGS[locale][key] ?? STRINGS.en[key]
}
